import { useCallback, useState } from 'react';
import toast from '../utils/toast';

/**
 * Shared guard for "discard + close" flows.
 * Ensures the UI can always close (fail-open) even when cleanup fails.
 */
export const useSafeDiscardAndClose = () => {
  const [isDiscarding, setIsDiscarding] = useState(false);

  const safeDiscardAndClose = useCallback(async ({
    discardAction,
    isIgnorableError = () => false,
    onBeforeClose,
    onDiscardSuccess,
    onClose,
    failureMessage = 'Could not fully discard this draft. Closing anyway; cleanup will be retried later.',
    failureDuration = 7000,
    logContext = 'Discard cleanup failed during modal close:',
  } = {}) => {
    let discardFailed = false;
    let discardSucceeded = false;

    if (typeof discardAction === 'function') {
      setIsDiscarding(true);
      try {
        await discardAction();
        discardSucceeded = true;
      } catch (error) {
        if (isIgnorableError(error)) {
          discardSucceeded = true;
        } else {
          discardFailed = true;
          console.warn(logContext, error);
        }
      } finally {
        setIsDiscarding(false);
      }
    }

    onBeforeClose?.();

    if (discardSucceeded) {
      onDiscardSuccess?.();
    }

    if (discardFailed && failureMessage) {
      toast.error(failureMessage, { duration: failureDuration });
    }

    onClose?.({ discardSucceeded, discardFailed });
  }, []);

  return {
    isDiscarding,
    safeDiscardAndClose,
  };
};

